import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, #1a365d 0%, #2d3748 50%, #1a365d 100%);
  color: #e2e8f0;
  padding: 4rem 2rem 2rem 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #4299e1, transparent);
  }
`;

const FooterContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 3rem;
  margin-bottom: 3rem;
`;

const FooterSection = styled.div`
  h3 {
    color: #4299e1;
    font-size: 1.2rem;
    font-weight: 500;
    margin-bottom: 1.5rem;
    letter-spacing: 1px;
  }
`;

const Logo = styled.div`
  font-size: 2rem;
  font-weight: 200;
  color: #e2e8f0;
  letter-spacing: 3px;
  margin-bottom: 1rem;

  .highlight {
    color: #4299e1;
    text-shadow: 0 0 15px rgba(66, 153, 225, 0.5);
  }
`;

const Description = styled.p`
  color: #a0aec0;
  line-height: 1.6;
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #a0aec0;
  font-size: 0.9rem;

  .icon {
    color: #4299e1;
    font-size: 1.1rem;
    width: 20px;
    text-align: center;
  }

  a {
    color: #a0aec0;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: #4299e1;
    }
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FooterLink = styled(Link)`
  color: #a0aec0;
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  padding: 0.25rem 0;

  &:hover {
    color: #4299e1;
    transform: translateX(5px);
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  background: rgba(66, 153, 225, 0.1);
  border: 1px solid rgba(66, 153, 225, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4299e1;
  font-size: 1.2rem;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(66, 153, 225, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(66, 153, 225, 0.2);
  }
`;

const Newsletter = styled.div`
  background: rgba(66, 153, 225, 0.08);
  border: 1px solid rgba(66, 153, 225, 0.2);
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;

  h4 {
    color: #4299e1;
    margin-bottom: 1rem;
    font-size: 1.1rem;
    font-weight: 500;
  }

  p {
    color: #a0aec0;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;

  input {
    flex: 1;
    min-width: 200px;
    padding: 0.75rem 1rem;
    border: 1px solid rgba(66, 153, 225, 0.3);
    border-radius: 25px;
    background: rgba(255, 255, 255, 0.05);
    color: #e2e8f0;
    font-size: 0.9rem;
    transition: all 0.3s ease;

    &::placeholder {
      color: #a0aec0;
    }

    &:focus {
      outline: none;
      border-color: #4299e1;
      box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
    }
  }

  button {
    background: linear-gradient(135deg, #3182ce, #4299e1);
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 25px;
    cursor: pointer;
    font-weight: 500;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    white-space: nowrap;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(66, 153, 225, 0.3);
    }
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatItem = styled.div`
  text-align: center;
  padding: 1rem;
  background: rgba(66, 153, 225, 0.05);
  border: 1px solid rgba(66, 153, 225, 0.2);
  border-radius: 12px;

  .number {
    font-size: 1.5rem;
    font-weight: 200;
    color: #4299e1;
    margin-bottom: 0.25rem;
  }

  .label {
    font-size: 0.8rem;
    color: #a0aec0;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(66, 153, 225, 0.2);
  padding-top: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  color: #a0aec0;
  font-size: 0.85rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const LegalLinks = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const LegalLink = styled(Link)`
  color: #a0aec0;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #4299e1;
  }
`;

const Footer: React.FC = () => {
  return (
    <FooterContainer>
      <FooterContent>
        {/* Brand & Description */}
        <FooterSection>
          <Logo>
            <span className="highlight">Arctic</span> Owl
          </Logo>
          <Description>
            Follow our historic world circumnavigation as we sail through the most challenging and beautiful 
            waters on Earth. Join us on this once-in-a-lifetime adventure.
          </Description>
          
          <Stats>
            <StatItem>
              <div className="number">13,200</div>
              <div className="label">Nautical Miles</div>
            </StatItem>
            <StatItem>
              <div className="number">4</div>
              <div className="label">Continents</div>
            </StatItem>
            <StatItem>
              <div className="number">12</div>
              <div className="label">Months</div>
            </StatItem>
          </Stats>

          {/* Main Sponsors */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2rem',
            margin: '2rem 0',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              flexDirection: window.innerWidth < 768 ? 'column' : 'row',
              textAlign: window.innerWidth < 768 ? 'center' : 'left'
            }}>
              
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              flexDirection: window.innerWidth < 768 ? 'column' : 'row',
              textAlign: window.innerWidth < 768 ? 'center' : 'left'
            }}>
              <span style={{ 
                color: '#4299e1', 
                fontSize: '0.9rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Sponsored by
              </span>
              <img 
                src="/images/Banco_Santander_Logotipo.svg.png" 
                alt="Banco Santander"
                style={{
                  height: window.innerWidth < 768 ? '30px' : '35px',
                  objectFit: 'contain'
                }}
              />
            </div>
          </div>
        </FooterSection>

        {/* Quick Links */}
        <FooterSection>
          <h3>Expedition</h3>
          <FooterLinks>
            <FooterLink to="/tracking">Live Tracking</FooterLink>
            <FooterLink to="/history">Yacht History</FooterLink>
            <FooterLink to="/team">Meet the Crew</FooterLink>
            {/* <FooterLink to="/sponsors">Our Partners</FooterLink> */}
            {/* <FooterLink to="/tourists">Join Adventure</FooterLink> */}
          </FooterLinks>
        </FooterSection>

        {/* Contact Info */}
        <FooterSection>
          <h3>Contact</h3>
          <ContactInfo>
            <ContactItem>
              <span className="icon">📧</span>
              <a href="mailto:info@arcticowl.com">info@arcticowl.com</a>
            </ContactItem>
            <ContactItem>
              <span className="icon">📱</span>
              <a href="tel:+34123456789">+34 123 456 789</a>
            </ContactItem>
            <ContactItem>
              <span className="icon">📍</span>
              Marina Salinas, Torrevieja, Spain
            </ContactItem>
            <ContactItem>
              <span className="icon">📡</span>
              Iridium: +881 6123 456 789
            </ContactItem>
          </ContactInfo>
        </FooterSection>

        {/* Newsletter */}
        <FooterSection>
          <Newsletter>
            <h4>Expedition Updates</h4>
            <p>
              Get real-time updates from our journey including weather reports, 
              crew stories, and exclusive behind-the-scenes content.
            </p>
            <NewsletterForm>
              <input 
                type="email" 
                placeholder="your@email.com" 
                required 
              />
              <button type="submit">Subscribe</button>
            </NewsletterForm>
          </Newsletter>
        </FooterSection>
      </FooterContent>

      <FooterBottom>
        <div>
          © 2024 Arctic Owl Expedition. All rights reserved.
        </div>
        <LegalLinks>
          <LegalLink to="/privacy">Privacy Policy</LegalLink>
          <LegalLink to="/terms">Terms of Service</LegalLink>
          <LegalLink to="/cookies">Cookie Policy</LegalLink>
        </LegalLinks>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer; 